/**
 * usePublicChecklist - Vistoria pelo link externo (/v/:token)
 *
 * A página pública nunca acessa o banco: toda comunicação passa pela Edge
 * Function `public-checklist` (mesmo padrão de `usePublicProposal`). A sessão
 * emitida após confirmar a identidade fica no sessionStorage da aba.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-14 v1.8.0 - uploadFile aceita onProgress (progresso real por XHR, com
 *            retorno ao envio padrão em caso de falha).
 *   RELEASE: 2026-10-13 v1.7.0 - generateAcceptanceLink: quem executa a vistoria pelo link
 *            externo gera o link exclusivo de aceite do cliente (action acceptance_link_generate);
 *            confirmAcceptance já repassa signer_kind (Titular/Testemunha).
 *   RELEASE: 2026-10-05 v1.6.0 - Inclui tipo do checklist no detalhe público
 *            para exibir a identificação completa no link externo.
 *   RELEASE: 2026-10-01 v1.5.0 - Nome do cliente/proprietário do veículo (owner_name) no
 *            resolve, exibido no header do link para confirmação visual de que o link
 *            pertence à pessoa certa. Fallback para o detalhe pós-confirmação.
 *   RELEASE: 2026-09-29 v1.4.0 - Encerramento local da sessão pública para permitir
 *            confirmar outra identidade sem revogar o link nem alterar a vistoria.
 *   RELEASE: 2026-09-28 v1.3.0 - Confirma documento do proprietário ou e-mail do usuário responsável.
 *   RELEASE: 2026-09-28 v1.2.0 - Foto modelo assinada na configuração dos itens visuais.
 *   RELEASE: 2026-09-27 v1.1.0 - Aceite pelo link (acceptance_prepare/acceptance_confirm).
 *   RELEASE: 2026-09-27 v1.0.0 - Criação inicial.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { ConfirmAcceptanceInput, PreparedAcceptance } from '@/hooks/useChecklistAcceptance';

const logger = createLogger('usePublicChecklist');

/**
 * v1.8.0: envio para URL assinada do Storage com progresso real (XMLHttpRequest).
 * Mesmo formato do cliente oficial (FormData + cacheControl); endereço vem do .env.
 */
function uploadWithProgress(path: string, signToken: string, file: File, onProgress: (p: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const base = import.meta.env.VITE_SUPABASE_URL as string;
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
    const url = `${base}/storage/v1/object/upload/sign/checklists/${path.split('/').map(encodeURIComponent).join('/')}?token=${encodeURIComponent(signToken)}`;
    const body = new FormData();
    body.append('cacheControl', '3600');
    body.append('', file);
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (key) { xhr.setRequestHeader('apikey', key); xhr.setRequestHeader('Authorization', `Bearer ${key}`); }
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? (onProgress(1), resolve()) : reject(new Error(`HTTP ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Falha de rede'));
    xhr.send(body);
  });
}

export type PublicLinkState = 'active' | 'submitted' | 'expired' | 'revoked' | 'closed' | 'invalid';

export interface PublicChecklistResolve {
  state: PublicLinkState;
  /** v1.6.0: 'acceptance' = link exclusivo de aceite do cliente */
  purpose?: 'execution' | 'acceptance';
  identity_kind?: 'document' | 'email';
  expires_at?: string;
  instance?: { code: number; status: string };
  tenant?: { name: string | null; logo_url: string | null; primary_color: string | null };
  vehicle?: { plate: string | null; brand: string | null; model: string | null; year: string | null; owner_name?: string | null };
  template_name?: string;
}

export interface PublicAttachment {
  storage_path: string; file_name: string; mime_type: string | null;
  size_bytes: number | null; sha256: string | null; from_camera: boolean; url?: string | null;
}

export interface PublicItem {
  answer_id: string;
  review_status: 'pending' | 'approved' | 'rejected';
  review_reason: string | null;
  item: {
    id: string; name: string; description: string | null; field_type: string; is_required: boolean;
    placeholder: string | null; default_value: unknown; version_section_id: string | null;
    config: {
      options: string[]; camera_only: boolean; max_files: number | null;
      reference_photo?: { file_name: string; mime_type: string; size_bytes: number; url: string | null } | null;
    };
  };
  current: { value: any; created_at: string } | null;
  attachments: PublicAttachment[];
}

export interface PublicChecklistDetail {
  instance: { code: number; status: string; template_name: string; checklist_type_name: string | null; due_finish_at: string | null };
  version: { version_number: number; settings: { geo_mode?: string; geo_required?: boolean; requires_review?: boolean } };
  vehicle: Record<string, string | null>;
  sections: { id: string; name: string; sort_order: number }[];
  items: PublicItem[];
}

/** Chamada genérica à Edge Function (lança erro com mensagem amigável). */
async function call<T = any>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('public-checklist', { body });
  if (error) throw new Error('Falha de comunicação. Verifique sua internet.');
  if (!data?.success) {
    const err = new Error(data?.error || 'Não foi possível concluir') as Error & { code?: string; remaining?: number };
    err.code = data?.code; err.remaining = data?.remaining;
    throw err;
  }
  return data.data as T;
}

/** SHA-256 hexadecimal de um arquivo (integridade da evidência). */
async function fileSha256(file: File) {
  const buf = await file.arrayBuffer();
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', buf))).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function usePublicChecklist(token: string | undefined) {
  const storageKey = `uaizy-vistoria-${token}`;
  const [session, setSession] = useState<string | null>(() => (token ? sessionStorage.getItem(storageKey) : null));
  const [resolved, setResolved] = useState<PublicChecklistResolve | null>(null);
  const [detail, setDetail] = useState<PublicChecklistDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const resolve = useCallback(async () => {
    if (!token) return;
    try {
      setResolved(await call<PublicChecklistResolve>({ action: 'resolve', token }));
    } catch (e) {
      logger.warn('Falha ao resolver link', { erro: (e as Error).message });
      setResolved({ state: 'invalid' });
    }
  }, [token]);

  const load = useCallback(async (s = session) => {
    if (!token || !s) return;
    try {
      setDetail(await call<PublicChecklistDetail>({ action: 'load', token, session: s }));
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === 'session') { sessionStorage.removeItem(storageKey); setSession(null); }
      throw e;
    }
  }, [token, session, storageKey]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await resolve();
      if (session) await load().catch(() => undefined);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyIdentity = async (identity: string) => {
    const r = await call<{ session: string }>({ action: 'verify_cpf', token, identity });
    sessionStorage.setItem(storageKey, r.session);
    setSession(r.session);
    await load(r.session);
  };

  /** Encerra somente a sessão temporária deste link e remove os dados protegidos da tela. */
  const exitSession = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setSession(null);
    setDetail(null);
  }, [storageKey]);

  /** Executa uma ação autenticada pela sessão e recarrega os dados. */
  const act = async (body: Record<string, unknown>) => {
    await call({ ...body, token, session });
    await Promise.all([load(), resolve()]);
  };

  /** Envia arquivo por URL assinada emitida pelo servidor e devolve os metadados. */
  const uploadFile = async (answerId: string, file: File, fromCamera: boolean, onProgress?: (p: number) => void): Promise<PublicAttachment> => {
    const up = await call<{ path: string; token: string }>({
      action: 'upload_url', token, session, answer_id: answerId, file_name: file.name, size_bytes: file.size,
    });
    // v1.8.0: com onProgress, envia por XHR para medir o progresso; se falhar, usa o cliente oficial.
    let sent = false;
    if (onProgress) {
      try { await uploadWithProgress(up.path, up.token, file, onProgress); sent = true; }
      catch (e) { logger.warn('Envio com progresso falhou; usando envio padrão', (e as Error)?.message); }
    }
    if (!sent) {
      const { error } = await supabase.storage.from('checklists').uploadToSignedUrl(up.path, up.token, file, { contentType: file.type });
      if (error) throw new Error('Falha ao enviar o arquivo');
      onProgress?.(1);
    }
    return { storage_path: up.path, file_name: file.name, mime_type: file.type, size_bytes: file.size, sha256: await fileSha256(file), from_camera: fromCamera };
  };

  // v1.1.0: aceites exigidos pelo modelo (resolvidos no servidor)
  const prepareAcceptance = useCallback(async () => {
    if (!token || !session) return null;
    return call<PreparedAcceptance & { participants?: Array<{ can_sign_here?: boolean }> }>({ action: 'acceptance_prepare', token, session });
  }, [token, session]);

  /** Registra o aceite pelo canal do link externo. */
  const confirmAcceptance = async (input: ConfirmAcceptanceInput) => {
    await call({ action: 'acceptance_confirm', token, session, ...input });
  };

  /**
   * v1.7.0: gera o link exclusivo de aceite do cliente a partir do link de execução.
   * O servidor revalida sessão, aceite pendente e documento do proprietário.
   */
  const generateAcceptanceLink = async () => {
    const r = await call<{ token: string; expires_at: string }>({ action: 'acceptance_link_generate', token, session });
    return { ...r, url: `${window.location.origin}/v/${r.token}` };
  };

  // v1.5.0: nome do cliente/proprietário (resolve cobre pré-confirmação; detalhe cobre depois)
  const ownerName = (resolved?.vehicle?.owner_name ?? (detail?.vehicle as { owner_name?: string | null } | undefined)?.owner_name) || null;

  /** Atualização manual sem alterar loading global, sessão ou montar novamente o formulário. */
  const refresh = useCallback(async () => {
    if (!token) return;
    const latest = await call<PublicChecklistResolve>({ action: 'resolve', token });
    if (session && latest.state === 'active') await load();
    setResolved(latest);
  }, [token, session, load]);

  return { prepareAcceptance, confirmAcceptance, generateAcceptanceLink, loading, resolved, detail, ownerName, hasSession: !!session, verifyIdentity, exitSession, act, uploadFile, reload: load, refresh };
}
